import { useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import { AlertErrorCircle, FilePdf } from '@/assets/icons'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { trackMedSummaryEvent } from '@/analytics/utils/medsummary-analytics'
import { MedSummaryAssetActions } from '../store/slice'
import { selectFailedFilesUpdate, selectIsUpdateActive, selectMutationError, selectMutationPending, selectUpdateStatusForAsset } from '../store/selectors'
import { MedSummaryUpdateStatus } from '../store/schemas'
import { activeStatusLabel } from '../utils/active-status-label'

interface MedSummaryAssetBannersProps {
  matterId: string
  assetId: string
  /** Opens the version-history panel — wired to the failed-files banner's "Version info" action. */
  onShowVersionInfo?: () => void
}

/**
 * Status banners for the living billing summary, rendered as full-width strips across the
 * top of the content area: in-progress update strip, unresolved FAILED_FILES decision strip
 * (retry failed / continue without them), failed-update notice, and inline mutation errors.
 */
export default function MedSummaryAssetBanners(props: MedSummaryAssetBannersProps) {
  const { matterId, assetId, onShowVersionInfo } = props
  const dispatch = useAppDispatch()
  // "Dismiss" on the error banner hides it for this session (keyed by update id).
  const [dismissedUpdateId, setDismissedUpdateId] = useState<string | null>(null)

  const updateStatus = useAppSelector((state: RootState) => selectUpdateStatusForAsset(state, { assetId }))
  const isUpdateActive = useAppSelector((state: RootState) => selectIsUpdateActive(state, { matterId }))
  const failedFilesUpdate = useAppSelector((state: RootState) => selectFailedFilesUpdate(state, { matterId }))
  const mutationPending = useAppSelector((state: RootState) => selectMutationPending(state, { assetId }))
  const mutationError = useAppSelector((state: RootState) => selectMutationError(state, { assetId }))

  const banners: React.ReactNode[] = []

  if (isUpdateActive) {
    const status = updateStatus?.update.status ?? MedSummaryUpdateStatus.PENDING
    banners.push(
      <div key="updating" className="flex items-center gap-1 pl-4 pr-2 py-2.5 bg-white border border-brand-neutral-200 rounded-md">
        <CircularProgressContinuousSized size={24} thickness={4} />
        <span className="text-xs font-medium text-brand-neutral-950">{activeStatusLabel(status, updateStatus?.progress)}</span>
      </div>
    )
  } else if (failedFilesUpdate) {
    // Unresolved FAILED_FILES: the run is holding with its staged successes unpublished.
    // Both resolution paths are offered here: retry the failed files, or continue without them.
    const failedNames = (failedFilesUpdate.failed_files ?? []).map((file) => file.display_name)
    banners.push(
      <div key="failed-files" className="flex flex-col overflow-hidden rounded-md border border-brand-neutral-200 bg-yellow-50">
        <div className="flex items-start gap-2.5 pl-4 pr-2 py-3">
          {/* Stacks below sm so the explainer keeps full width instead of wrapping in a sliver
              beside the nowrap headline. */}
          <div className="flex-1 min-w-0 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
            <span className="flex items-center gap-1 shrink-0">
              <AlertErrorCircle className="w-6 h-6 shrink-0 text-yellow-800" />
              <span className="text-xs font-medium text-yellow-800 whitespace-nowrap">Some files couldn&rsquo;t be added.</span>
            </span>
            <span className="text-xs font-medium text-brand-neutral-950">
              The files below weren&rsquo;t processed &mdash; retry them, or continue without them to publish the rest.
            </span>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {onShowVersionInfo && (
              <button onClick={() => onShowVersionInfo()} className="px-2 py-0.5 rounded-[4px] text-xs font-medium text-brand-neutral-950 hover:bg-yellow-100">
                Version info
              </button>
            )}
            <button
              onClick={() => {
                trackMedSummaryEvent(AnalyticsEvent.MedSummaryAssetRetryFailedClicked, { medSummaryAssetId: assetId })
                dispatch(MedSummaryAssetActions.retryFailedFiles({ assetId, updateId: failedFilesUpdate.update.id }))
              }}
              disabled={mutationPending}
              className="px-2 py-0.5 rounded-[4px] text-xs font-medium text-brand-neutral-950 hover:bg-yellow-100 disabled:opacity-60"
            >
              Retry failed files
            </button>
            <button
              onClick={() => {
                trackMedSummaryEvent(AnalyticsEvent.MedSummaryAssetCompleteExcludingFailedClicked, { medSummaryAssetId: assetId })
                dispatch(MedSummaryAssetActions.completeExcludingFailed({ assetId, updateId: failedFilesUpdate.update.id }))
              }}
              disabled={mutationPending}
              className="px-2 py-0.5 rounded-[4px] text-xs font-medium text-brand-neutral-950 hover:bg-yellow-100 disabled:opacity-60"
            >
              Continue without them
            </button>
          </div>
        </div>
        {failedNames.length > 0 && (
          <div className="flex flex-col border-t border-brand-neutral-200 bg-white py-1">
            {failedNames.map((name, index) => (
              <div key={`${name}-${index}`} className="flex h-9 items-center gap-2 px-4">
                <FilePdf className="w-5 h-5 shrink-0 text-brand-neutral-800" />
                <span className="min-w-0 break-words text-sm tracking-[-0.15px] text-brand-neutral-950">{name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  } else if (updateStatus?.update.status === MedSummaryUpdateStatus.ERROR && updateStatus.update.id !== dismissedUpdateId) {
    // Red strip, single "Dismiss" — the live billing summary was not changed by the failed run.
    const errorUpdateId = updateStatus.update.id
    banners.push(
      <div key="error" className="flex items-start gap-2.5 pl-4 pr-2 py-2.5 bg-red-50 border border-brand-neutral-200 rounded-md">
        <div className="flex-1 min-w-0 flex flex-col items-start gap-1 sm:flex-row sm:items-center sm:gap-2">
          <span className="flex items-center gap-1 shrink-0">
            <AlertErrorCircle className="w-6 h-6 shrink-0 text-red-800" />
            <span className="text-xs font-medium text-red-800 whitespace-nowrap">Files couldn&rsquo;t be added.</span>
          </span>
          <span className="text-xs font-normal text-brand-neutral-950">The existing entries and sources weren&rsquo;t affected - please try again.</span>
        </div>
        <button
          onClick={() => setDismissedUpdateId(errorUpdateId)}
          className="shrink-0 px-2 py-0.5 rounded-[4px] text-xs font-medium text-brand-neutral-950 hover:bg-red-100"
        >
          Dismiss
        </button>
      </div>
    )
  }

  if (mutationError) {
    // Aligned to the Update Error banner's treatment for consistency.
    banners.push(
      <div key="mutation-error" className="flex items-center gap-2.5 pl-4 pr-2 py-2.5 bg-red-50 border border-brand-neutral-200 rounded-md">
        <AlertErrorCircle className="w-6 h-6 shrink-0 text-red-800" />
        <span className="text-xs font-medium text-red-800">{mutationError}</span>
      </div>
    )
  }

  if (banners.length === 0) return null

  // Separate the status strip from the tabs below. Margin lives here so no gap shows when
  // there's no banner.
  return <div className="flex flex-col mb-4 lg:mb-5">{banners}</div>
}
